import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Card } from '@/components/ui-kit/card';
import {
  DescriptionDetails,
  DescriptionItem,
  DescriptionList,
  DescriptionTerm,
} from '@/components/ui-kit/description-list';
import { cn } from '@/lib/utils';

interface ProfileSectionCardProps {
  title: string;
  description?: ReactNode;
  /** Where the section's pencil goes (the edit page on the section's tab); no pencil without it. */
  editTo?: string;
  className?: string;
  children: ReactNode;
}

/**
 * One section of a profile (prototype `ovCard`, Component Map › Employee details › Overview): a small card
 * with its title, an optional description and its own Edit pencil, holding a description list.
 */
export const ProfileSectionCard = ({
  title,
  description,
  editTo,
  className,
  children,
}: ProfileSectionCardProps) => {
  const { t } = useTranslation();
  const editLabel = t('EDIT_SECTION', { section: title.toLowerCase() });
  return (
    <Card className={cn('flex h-full min-w-0 flex-col gap-2 p-4 shadow-none', className)}>
      <div className="flex items-start justify-between gap-2">
        <div className="grid min-w-0 gap-0.5">
          <h2 className="text-[15px] font-semibold">{title}</h2>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
        {editTo && (
          <Button asChild variant="ghost" size="icon-sm" className="-mr-1 -mt-1 shrink-0">
            <Link to={editTo} aria-label={editLabel} title={editLabel}>
              <Pencil />
            </Link>
          </Button>
        )}
      </div>
      <div className="min-w-0">{children}</div>
    </Card>
  );
};

/** Profile cards side by side at equal heights; one column below 1180px (prototype `.ov-grid`). */
export const ProfileCardGrid = ({ children }: { children: ReactNode }) => (
  <div className="grid grid-cols-1 items-stretch gap-4 min-[1180px]:grid-cols-2">{children}</div>
);

export type Fact = [label: ReactNode, value: ReactNode];

/** A section's facts as a description list (TBL-01); a missing value shows a dash. */
export const FactList = ({ facts }: { facts: Fact[] }) => (
  <DescriptionList>
    {facts.map(([label, value], index) => (
      <DescriptionItem key={index}>
        <DescriptionTerm>{label}</DescriptionTerm>
        {/* `||`, not `??`: date formatting yields '' for a missing date. */}
        <DescriptionDetails>{value || '-'}</DescriptionDetails>
      </DescriptionItem>
    ))}
  </DescriptionList>
);
